"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * VisuallyHidden component for accessibility
 * Makes content available to screen readers but visually hidden
 */
const VisuallyHidden = ({ children }: { children: React.ReactNode }) => (
  <span 
    className="absolute w-px h-px p-0 -m-1 overflow-hidden whitespace-nowrap border-0" 
    style={{ clip: 'rect(0, 0, 0, 0)' }}
  >
    {children}
  </span>
)

/**
 * Context for DialogContent to communicate with DialogTitle and DialogDescription
 * Ensures accessibility requirements are met throughout the component hierarchy
 */
interface DialogContentContextValue {
  // Unique ID for the description element
  descriptionId: string;
  // Unique ID for the title element
  titleId: string;
  // Tracks if a description element exists
  hasDescription: boolean;
  // Updates the description existence state
  setHasDescription: React.Dispatch<React.SetStateAction<boolean>>;
  // Tracks if a title element exists
  hasTitle: boolean;
  // Updates the title existence state
  setHasTitle: React.Dispatch<React.SetStateAction<boolean>>;
}

const DialogContentContext = React.createContext<DialogContentContextValue>({
  descriptionId: '',
  titleId: '',
  hasDescription: false,
  setHasDescription: () => {},
  hasTitle: false,
  setHasTitle: () => {},
});

// Export primitive components from Radix UI
const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogClose = DialogPrimitive.Close

// Custom DialogPortal with enhanced styling and responsive layout
type DialogPortalProps = DialogPrimitive.DialogPortalProps & {
  // Allow passing className for styling flexibility
  className?: string
}

const DialogPortal = ({
  className,
  children,
  ...props
}: DialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center", 
      className
    )}>
      {children}
    </div>
  </DialogPrimitive.Portal>
)

DialogPortal.displayName = DialogPrimitive.Portal.displayName

// Enhanced DialogOverlay with animation and styling
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Enhanced DialogContent component with:
 * - Proper accessibility for screen readers
 * - Automatic title generation when missing
 * - Responsive sizing based on viewport
 * - Animation when opening/closing
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  // Generate unique IDs for accessibility
  const descriptionId = React.useId();
  const titleId = React.useId();
  
  // Track whether description and title elements exist
  const [hasDescription, setHasDescription] = React.useState(false);
  const [hasTitle, setHasTitle] = React.useState(false);
  
  // Use the provided aria-describedby or fallback to generated ID
  const describedBy = props["aria-describedby"] || descriptionId;
  
  // Use a ref to detect if component is mounted
  const mountedRef = React.useRef(false);
  
  // Responsive sizing state and logic
  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  
  // Update window width on resize
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  // Determine max width based on screen size
  const getMaxWidth = () => {
    if (windowWidth < 640) return "95vw"; // Mobile
    if (windowWidth < 1024) return "85vw"; // Tablet
    return "65vw"; // Desktop
  };
  
  // Development-only warning for missing title
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && mountedRef.current && !hasTitle) {
      console.warn(
        'Dialog accessibility warning: DialogContent requires a DialogTitle as a direct child for screen reader accessibility.'
      );
    }
    
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
    };
  }, [hasTitle]);
  
  // Context value for child components
  const contextValue = React.useMemo(() => ({
    descriptionId,
    titleId,
    hasDescription,
    setHasDescription,
    hasTitle,
    setHasTitle,
  }), [descriptionId, titleId, hasDescription, hasTitle]);
  
  return (
    <DialogContentContext.Provider value={contextValue}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg md:w-auto",
            className
          )}
          style={{
            maxWidth: props.style?.maxWidth || getMaxWidth(),
            maxHeight: props.style?.maxHeight || "85vh",
          }}
          data-testid="dialog-content"
          {...props}
          aria-describedby={hasDescription ? describedBy : undefined}
          aria-labelledby={titleId}
        >
          {/* Automatically add a title element for screen readers if none exists */}
          {!hasTitle && (
            <DialogPrimitive.Title 
              id={titleId} 
              className="sr-only" 
              data-testid="auto-dialog-title"
            >
              {props["aria-label"] || "Dialog Content"}
            </DialogPrimitive.Title>
          )}
          
          {children}
          
          <DialogPrimitive.Close 
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            data-testid="dialog-close-button"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogContentContext.Provider>
  );
})

DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
))
DialogHeader.displayName = "DialogHeader"

const DialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
))
DialogFooter.displayName = "DialogFooter"

/**
 * DialogTitle component with enhanced accessibility features
 * Automatically sets its ID and adds aria-labelledby
 * Registers itself with parent DialogContent for proper accessibility validation
 */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const { titleId, setHasTitle } = React.useContext(DialogContentContext);

  // Register presence of title with parent DialogContent on mount/unmount
  React.useEffect(() => {
    setHasTitle(true);
    return () => setHasTitle(false);
  }, [setHasTitle]);

  return (
    <DialogPrimitive.Title
      ref={ref}
      id={props.id || titleId}
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  )
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

/**
 * DialogDescription component that automatically registers with DialogContent
 * for proper accessibility handling with aria-describedby
 */
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const { descriptionId, setHasDescription } = React.useContext(DialogContentContext);

  // Register presence with parent DialogContent
  React.useEffect(() => {
    setHasDescription(true);
    return () => setHasDescription(false);
  }, [setHasDescription]);

  return (
    <DialogPrimitive.Description
      ref={ref}
      id={props.id || descriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
})
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}